import './table-hac.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import decodeToken from '../../../../utils/decode-token';
import {Alert, Snackbar} from '@mui/material';
import SectionTabs from '../../common/section-tabs/section-tabs';
import TableHacFilters from './table-hac-filters';
import TableHacRow from './table-hac-row';
import SortableHeaderCell from '../../common/table/sortable-header-cell';
import handleEmptyTableResponse from '../../common/table/utils/handle-empty-table-response';
import { ROLES, MARKETS } from '../../../../constants/constants';
import { fetchAdminHacs, fetchAdminPmas, fetchAdminUserById, deleteNominee } from '../../../../api/adminHacTableApi';
import ModalWindow from '../../common/modal/modal.js';
import {
  INITIAL_FILTERS,
  dedupeById,
  getEmailStatusLabel,
  sortHacRows,
  SORT_FIELDS,
  sortByLastUpdateDesc,
  sortCountriesByLabel,
  ADMIN_HAC_FILTERS_KEY,
} from '../../../../utils/adminHacTableUtils';
import {
  getNextSortDirection,
  getStoredFilters,
  pluckUniqueTruthyValues,
  saveFilters,
} from '../../../../utils/tableUtils';
import { isAdminRole } from '../../../../utils/role-utils';

const PMA_FILTER_VISIBLE = false;


function TableHac({
  reload,
  onDataLoaded = () => {},
  statusFilter = '',
  onStatusFilterChange = () => {},
  onSummaryChange = () => {},
}) {
  const {t} = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const decodedToken = decodeToken(token);
  const role = decodedToken.role;
  const isAdminLikeRole = isAdminRole(role);
  const navigate = useNavigate();
  const [dataLoaded, setDataLoaded] = useState(false);
  const userId = decodedToken.id;
  const [loggedUser] = useState(
    () => JSON.parse(sessionStorage.getItem('loggedUser') || 'null')
  );


  const [hacs, setHacs] = useState([]);
  const [filteredHacs, setFilteredHacs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [pmas, setPmas] = useState([]);
  const showPmaFilter = PMA_FILTER_VISIBLE && role === ROLES.Admin;
  const markets = useMemo(() => {
    const userMarkets = loggedUser?.market || [];
    return MARKETS.filter((market) => userMarkets.includes(market.name));
  }, [loggedUser]);

  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [nomineeToDelete, setNomineeToDelete] = useState(null);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleOpenDeleteModal = (hacId) => {
    setNomineeToDelete(hacId);
    setDeleteModalOpen(true);
  };

  const handleDeleteNominee = async () => {
    if (!nomineeToDelete) {
      return;
    }

    try {
      await deleteNominee(token, nomineeToDelete);
      setSnackbarSeverity('success');
      setSnackbarMessage(t('admin-delete-nominee-success'));
      setSnackbarOpen(true);
      setDataLoaded(false);
    } catch (error) {
      setSnackbarSeverity('error');
      setSnackbarMessage(t('common-internal-server-error'));
      setSnackbarOpen(true);
    } finally {
      setDeleteModalOpen(false);
      setNomineeToDelete(null);
    }
  };

  const applyFilters = useCallback((appliedFilters = filters, hacsData = hacs) => {
    let filtered = [...hacsData];

    if (appliedFilters.name) {
      const searchTerm = appliedFilters.name.toLowerCase();
      filtered = filtered.filter(
        (hac) =>
          (hac.name || '').toLowerCase().includes(searchTerm) ||
          (hac.last_name || '').toLowerCase().includes(searchTerm)
      );
    }

    if (appliedFilters.market) {
      const selectedMarket = markets.find(
        (market) => market.name === appliedFilters.market
      );
      if (selectedMarket) {
        filtered = filtered.filter((hac) =>
          selectedMarket.countries.includes(hac.area_code)
        );
      }
    }

    if (appliedFilters.country) {
      filtered = filtered.filter((hac) => hac.area_code === appliedFilters.country);
    }

    if (appliedFilters.retailer) {
      filtered = filtered.filter((hac) => hac.retailer === appliedFilters.retailer);
    }

    if (PMA_FILTER_VISIBLE && appliedFilters.pma) {
      filtered = filtered.filter((hac) => hac.first_pma_email === appliedFilters.pma);
    }

    const effectiveStatus = statusFilter || appliedFilters.status;

    if (effectiveStatus === 'nominated' && appliedFilters.invitation) {
      filtered = filtered.filter(
        (hac) => getEmailStatusLabel(hac) === appliedFilters.invitation
      );
    }

    const summaryRows = dedupeById(filtered);

    if (effectiveStatus) {
      filtered = filtered.filter((hac) => hac.status === effectiveStatus);
    }

    const uniqueFiltered = dedupeById(filtered);
    setFilteredHacs(uniqueFiltered);

    const summaryCounts = summaryRows.reduce(
      (counts, hac) => ({
        ...counts,
        [hac.status]: (counts[hac.status] || 0) + 1,
      }),
      {}
    );

    const nominated = summaryCounts.nominated || 0;
    const active = summaryCounts.active || 0;

    onSummaryChange({
      active,
      nominated,
      total: nominated + active,
    });

    saveFilters(ADMIN_HAC_FILTERS_KEY, appliedFilters);
  }, [filters, hacs, markets, onSummaryChange, statusFilter]);

  useEffect(() => {
    const fetchData = async () => {
      if (!token || role !== ROLES.MarketAdmin) {
        return;
      }

      try {
        const response = await fetchAdminUserById(token, userId);
        if (!response.data?.market_area_code) {
          console.warn('No se encontró market_area_code en la respuesta.');
        }
      } catch (error) {
        setSnackbarOpen(true);
        setSnackbarMessage(t('common-internal-server-error'));
      }
    };

    fetchData();
    // eslint-disable-next-line
  }, [token, userId, role, t]);

  const fetchData = useCallback(async () => {
    if (!token) {
      console.error('No se encontró ningún token en sessionStorage.');
      return;
    }

    try {
      const response = await fetchAdminHacs(token);
      if (response.status === 200) {
        const sortedHacs = sortByLastUpdateDesc(response.data);
        const storedFilters = getStoredFilters(ADMIN_HAC_FILTERS_KEY, INITIAL_FILTERS);
        const initialFilters = PMA_FILTER_VISIBLE
          ? storedFilters
          : { ...storedFilters, pma: '' };

        setFilters(initialFilters);
        setHacs(sortedHacs);
        applyFilters(initialFilters, sortedHacs);

        const market = markets.find((m) => m.name === initialFilters.market);
        const availableCountries = market ? market.countries : [...new Set(response.data.map((hac) => hac.area_code))];

        const uniqueRetailers = pluckUniqueTruthyValues(response.data, 'retailer', { trimStrings: true });
        setRetailers(uniqueRetailers);
        setCountries(sortCountriesByLabel(availableCountries, t));

        if (showPmaFilter) {
          try {
            const pmasResponse = await fetchAdminPmas(token);
            if (pmasResponse.status === 200) {
              setPmas(pmasResponse.data);
            }
          } catch (error) {
            // La lista de PMAs es solo para el filtro, no bloquea la carga de la tabla.
          }
        }

        setDataLoaded(true);
        onDataLoaded();
      } else if (response.status === 204) {
        handleEmptyTableResponse({
          clearStateSetters: [setHacs, setFilteredHacs, setCountries, setRetailers],
          onDataLoaded,
          onSummaryChange,
          setDataLoaded,
        });
      }
    } catch (error) {
      if (error.response) {
        setSnackbarOpen(true);
        setSnackbarMessage(t('common-internal-server-error'));
      }
    }
  }, [applyFilters, markets, onDataLoaded, onSummaryChange, showPmaFilter, t, token]);

  useEffect(() => {
    if (!dataLoaded || reload) {
      fetchData();
    }
  }, [dataLoaded, fetchData, reload]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const nextFilters = { ...filters };
    const allCountries = [...new Set(hacs.map((hac) => hac.area_code))];
    const allRetailers = pluckUniqueTruthyValues(hacs, 'retailer', { trimStrings: true });

    if (name === 'market') {
      if (value === '') {
        setCountries(sortCountriesByLabel(allCountries, t));
        setRetailers(allRetailers);

        nextFilters.market = '';
        nextFilters.country = '';
        nextFilters.retailer = '';
      } else {
        const selectedMarket = markets.find((market) => market.name === value);
        const updatedCountries = selectedMarket ? selectedMarket.countries : allCountries;
        const filteredRetailers = pluckUniqueTruthyValues(
          hacs.filter((hac) => selectedMarket?.countries.includes(hac.area_code)),
          'retailer',
          { trimStrings: true }
        );

        setCountries(sortCountriesByLabel(updatedCountries, t));
        setRetailers(filteredRetailers);

        nextFilters.market = value;
        nextFilters.country = updatedCountries.length === 1 ? updatedCountries[0] : '';
        nextFilters.retailer = '';
      }
    } else if (name === 'country') {
      if (value === '') {
        const currentMarket = markets.find((market) => market.name === filters.market);
        const retailersForMarket = currentMarket
          ? pluckUniqueTruthyValues(
            hacs.filter((hac) => currentMarket.countries.includes(hac.area_code)),
            'retailer',
            { trimStrings: true }
          )
          : allRetailers;

        setRetailers(retailersForMarket);

        nextFilters.country = '';
        nextFilters.retailer = '';
      } else {
        const relatedMarket = markets.find((market) => market.countries.includes(value))?.name || filters.market;
        const filteredRetailers = pluckUniqueTruthyValues(
          hacs.filter((hac) => hac.area_code === value),
          'retailer',
          { trimStrings: true }
        );

        setRetailers(filteredRetailers);

        nextFilters.country = value;
        nextFilters.market = relatedMarket;
        nextFilters.retailer = '';
      }
    } else if (name === 'retailer') {
      const relatedCountries = [...new Set(
        hacs
          .filter((hac) => hac.retailer === value)
          .map((hac) => hac.area_code)
          .filter(Boolean)
      )];

      const relatedMarkets = markets
        .filter((market) => market.countries.some((country) => relatedCountries.includes(country)))
        .map((market) => market.name);

      nextFilters.retailer = value;
      nextFilters.country = relatedCountries.length === 1 ? relatedCountries[0] : filters.country;
      nextFilters.market = relatedMarkets.length === 1 ? relatedMarkets[0] : filters.market;
    } else {
      nextFilters[name] = value;
    }

    setFilters(nextFilters);
  };



  useEffect(() => {
    if (dataLoaded) {
      applyFilters(filters, hacs);
    }
  }, [applyFilters, dataLoaded, filters, hacs]);

  const handleApplyFilters = () => {
    applyFilters();
  };

  const handleResetFilters = () => {
    const initialFilters = INITIAL_FILTERS;

    const allCountries = [...new Set(hacs.map((hac) => hac.area_code))];
    const allRetailers = pluckUniqueTruthyValues(hacs, 'retailer', { trimStrings: true });

    setFilters(initialFilters);
    setCountries(sortCountriesByLabel(allCountries, t));
    setRetailers(allRetailers);

    saveFilters(ADMIN_HAC_FILTERS_KEY, initialFilters);

    applyFilters(initialFilters, hacs);
  };

  const getEmailStatus = (row) => {
    const emailStatusKey = getEmailStatusLabel(row);
    return emailStatusKey ? t(emailStatusKey) : '';
  };

  const handleRequestSort = (property) => {
    setOrder(getNextSortDirection(orderBy, order, property));
    setOrderBy(property);
  };

  const sortData = (array) => sortHacRows(array, orderBy, order);
  const statusTabs = [
    { id: 'active', label: t('common-active') },
    { id: 'nominated', label: t('common-nominated') },
    { id: 'exited', label: t('common-exited') },
  ];
  const showStatusColumn = !statusFilter;
  const showInvitationFilter = statusFilter === 'nominated';
  const showInvitationColumn = !statusFilter || statusFilter === 'nominated';

  return (
    <>
      {statusFilter ? (
        <SectionTabs
          tabs={statusTabs}
          activeTab={statusFilter}
          onTabClick={onStatusFilterChange}
          className="rrc-member-status-tabs"
        />
      ) : null}
      {dataLoaded ? (
        hacs && hacs.length !== 0 ? (
          <>
            <TableHacFilters
              t={t}
              showMarketFilter={(isAdminLikeRole || role === ROLES.MarketAdmin) && markets.length > 1}
              showCountryFilter={(isAdminLikeRole || role === ROLES.MarketAdmin) && (markets.length > 1 || countries.length > 1)}
              markets={markets}
              loggedUser={loggedUser}
              countries={countries}
              retailers={retailers}
              showPmaFilter={showPmaFilter}
              pmas={pmas}
              filters={filters}
              showStatusFilter={!statusFilter}
              showInvitationFilter={showInvitationFilter}
              onFilterChange={handleFilterChange}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
              filteredHacs={filteredHacs}
            />

            <div className="table-wrapper">
              {filteredHacs.length !== 0 ? (
                <Table
                  sx={{minWidth: 1180}}
                  aria-label="simple table"
                  className="user-table admin-hac"
                >
                  <TableHead>
                    <TableRow>
                      <SortableHeaderCell
                        field={SORT_FIELDS.NAME}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-name')}
                      />
                      <SortableHeaderCell
                        field={SORT_FIELDS.LAST_NAME}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-last-name')}
                      />
                      <SortableHeaderCell
                        field={SORT_FIELDS.PMA_EMAIL}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-role-pma')}
                      />
                      <TableCell align="left">
                        <span className="text">{t('common-retailer')}</span>
                      </TableCell>
{/*                      <TableCell align="left">
                        <span className="text">{t('common-market')}</span>
                      </TableCell>*/}
                      <TableCell align="left">
                        <span className="text">{t('common-country')}</span>
                      </TableCell>
                      <TableCell align="left">
                        <span className="text">{t('common-language')}</span>
                      </TableCell>
                      {showStatusColumn && (
                        <TableCell align="left">
                          <span className="text">{t('common-status')}</span>
                        </TableCell>
                      )}
                      {showInvitationColumn && (
                        <SortableHeaderCell
                          field={SORT_FIELDS.INVITATION}
                          orderBy={orderBy}
                          order={order}
                          onRequestSort={handleRequestSort}
                          label={t('common-invitation')}
                        />
                      )}
                      <SortableHeaderCell
                        field={SORT_FIELDS.LAST_UPDATE}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-last-update')}
                      />
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortData(filteredHacs).map((row) => (
                      <TableHacRow
                        key={row.id}
                        row={row}
                        t={t}
                        navigate={navigate}
                        emailStatus={getEmailStatus(row)}
                        showStatusColumn={showStatusColumn}
                        showInvitationColumn={showInvitationColumn}
                        statusFilter={statusFilter}
                        onDelete={handleOpenDeleteModal}
                      />
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="no-result">
                  <h4 className="txt-center">{t('admin-main-title-empty')}</h4>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="no-result">
            <h2 className="txt-center">
              {t('admin-main-tab1-title-empty')}
              <small>{t('admin-main-tab1-text-empty')}</small>
            </h2>
          </div>
        )
      ) : (
        <div>{t('common-loading')}</div>
      )}
      <ModalWindow
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        executeFunction={handleDeleteNominee}
        postExecuteFunction={() => {}}
        title={t('admin-delete-nominee-title')}
        text={t('admin-delete-nominee-text')}
        textButtonCancel={t('common-cancel')}
        textoButtonConfirm={t('common-remove')}
        extraInfo={[]}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TableHac;
